import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../providers/UserProvider';
import { ReaderNavbar } from '../components/ReaderNavbar';
import { setSavedSearchPresence } from '../utils/savedSearchPresence';
import { Toast } from '../components/Toast';
import {
  listSavedSearches,
  createSavedSearch,
  updateSavedSearch,
  deleteSavedSearch,
  type SavedSearch,
} from '../repositories/savedSearchRepository';
import { formatRelativeTime } from '../utils/formatTime';
import { IconCopy } from '../components/icons';
import { DigestSubscriptionMenu } from '../components/DigestSubscriptionMenu';
import {
  listDigests,
  listDigestCadences,
  type Digest,
  type DigestCadenceOption,
} from '../repositories/digestRepository';
import {
  DECAY_MAX,
  DECAY_MIN,
  DECAY_SLIDER_STEP,
  formatDecay,
  formatRelevanceMultiplier,
  RELEVANCE_MULT_MAX,
  RELEVANCE_MULT_MIN,
  RELEVANCE_SLIDER_STEP,
  SAVED_SEARCH_DEFAULT_DECAY,
  SAVED_SEARCH_DEFAULT_RELEVANCE,
} from '../scoringConfig';

function EditRow({
  search,
  onSave,
  onCancel,
  saving,
}: {
  search: SavedSearch | null;
  onSave: (values: { name: string; description: string; freshness_constant: number; relevance_constant: number }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(search?.name ?? '');
  const [description, setDescription] = useState(search?.description ?? '');
  const [decay, setDecay] = useState(search?.freshness_constant ?? SAVED_SEARCH_DEFAULT_DECAY);
  const [relevance, setRelevance] = useState(search?.relevance_constant ?? SAVED_SEARCH_DEFAULT_RELEVANCE);

  return (
    <div className="saved-search-edit-form">
      <div className="saved-search-edit-field">
        <label className="saved-search-edit-label">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          className="saved-search-edit-input"
          autoFocus={!search}
        />
      </div>
      <div className="saved-search-edit-field">
        <label className="saved-search-edit-label">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          disabled={saving}
          className="saved-search-edit-textarea"
        />
      </div>
      <div className="saved-search-edit-row">
        <div className="saved-search-edit-field" style={{ flex: 1 }}>
          <label className="saved-search-edit-label">Recency decay</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min={DECAY_MIN} max={DECAY_MAX} step={DECAY_SLIDER_STEP}
              value={decay}
              onChange={(e) => setDecay(parseFloat(e.target.value))}
              disabled={saving}
              style={{ flex: 1 }}
            />
            <code style={{ fontSize: '0.82rem', minWidth: 48 }}>{formatDecay(decay)}</code>
          </div>
        </div>
        <div className="saved-search-edit-field" style={{ flex: 1 }}>
          <label className="saved-search-edit-label">Relevance weighting</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min={RELEVANCE_MULT_MIN} max={RELEVANCE_MULT_MAX} step={RELEVANCE_SLIDER_STEP}
              value={relevance}
              onChange={(e) => setRelevance(parseFloat(e.target.value))}
              disabled={saving}
              style={{ flex: 1 }}
            />
            <code style={{ fontSize: '0.82rem', minWidth: 48 }}>{formatRelevanceMultiplier(relevance)}</code>
          </div>
        </div>
      </div>
      <div className="saved-search-edit-actions">
        <button
          type="button"
          className="primary-button"
          onClick={() => onSave({ name: name.trim(), description: description.trim(), freshness_constant: decay, relevance_constant: relevance })}
          disabled={saving || !name.trim() || !description.trim()}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" className="ghost-button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function SavedSearchesPage() {
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  // Digests are keyed by saved-search id so each row can show its own
  // subscription without a request per row.
  const [digestsBySearch, setDigestsBySearch] = useState<Record<string, Digest>>({});
  const [cadences, setCadences] = useState<DigestCadenceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ message: '', isError: false });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadDigests = useCallback(async () => {
    const rows = await listDigests();
    setDigestsBySearch(Object.fromEntries(rows.map((d) => [d.description_id, d])));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, loadedCadences] = await Promise.all([
        listSavedSearches(),
        listDigestCadences(),
      ]);
      setSearches(data);
      setSavedSearchPresence(data.length > 0);
      setCadences(loadedCadences);
      await loadDigests();
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to load', isError: true });
    } finally {
      setLoading(false);
    }
  }, [loadDigests]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (values: { name: string; description: string; freshness_constant: number; relevance_constant: number }) => {
    setSaving(true);
    try {
      const created = await createSavedSearch({ ...values, feed_enabled: true });
      setSearches((prev) => [created, ...prev]);
      setSavedSearchPresence(true);
      setCreating(false);
      setStatus({ message: 'Search created', isError: false });
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Create failed', isError: true });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string, values: { name: string; description: string; freshness_constant: number; relevance_constant: number }) => {
    setSaving(true);
    try {
      const updated = await updateSavedSearch(id, values);
      setSearches((prev) => prev.map((s) => s.id === id ? updated : s));
      setEditingId(null);
      setStatus({ message: 'Search updated', isError: false });
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Update failed', isError: true });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteSavedSearch(id);
      setSearches((prev) => {
        const remaining = prev.filter((s) => s.id !== id);
        setSavedSearchPresence(remaining.length > 0);
        return remaining;
      });
      setStatus({ message: 'Search deleted', isError: false });
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Delete failed', isError: true });
    } finally {
      setDeletingId(null);
    }
  };

  const handleFeedEnabled = async (search: SavedSearch, enabled: boolean) => {
    try {
      const updated = await updateSavedSearch(search.id, { feed_enabled: enabled });
      setSearches((prev) => prev.map((s) => s.id === search.id ? updated : s));
      setStatus({ message: enabled ? 'Feed enabled' : 'Feed stopped', isError: false });
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Feed update failed', isError: true });
    }
  };

  const copyFeedUrl = async (id: string) => {
    await navigator.clipboard.writeText(`${window.location.origin}/feed/${id}`);
    setStatus({ message: 'Feed URL copied', isError: false });
  };

  const dismissStatus = useCallback(() => setStatus({ message: '', isError: false }), []);

  return (
    <div className="reader-shell">
      <Toast message={status.message} isError={status.isError} onDismiss={dismissStatus} />
      <ReaderNavbar
        selectedChannelId=""
        onSelectChannel={() => {}}
        user={user}
        onLogout={logout}
      />
      <main className="reader-main-full">
        <section className="feed-list-pane">
          <div className="saved-searches-page">
            <div className="saved-searches-header">
              <h1 className="saved-searches-title">My feeds</h1>
              {!creating && (
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => { setCreating(true); setEditingId(null); }}
                >
                  New feed
                </button>
              )}
            </div>

            {creating && (
              <div className="saved-search-card saved-search-card-editing">
                <EditRow
                  search={null}
                  onSave={handleCreate}
                  onCancel={() => setCreating(false)}
                  saving={saving}
                />
              </div>
            )}

            {loading ? (
              <div className="empty-panel">Loading...</div>
            ) : searches.length === 0 && !creating ? (
              <div className="empty-panel">No feeds yet.</div>
            ) : (
              <div className="saved-searches-list">
                {searches.map((s) => (
                  <div key={s.id} className={`saved-search-card${editingId === s.id ? ' saved-search-card-editing' : ''}`}>
                    {editingId === s.id ? (
                      <EditRow
                        search={s}
                        onSave={(values) => handleUpdate(s.id, values)}
                        onCancel={() => setEditingId(null)}
                        saving={saving}
                      />
                    ) : (
                      <div className="saved-search-card-display">
                        <div className="saved-search-card-main">
                          <button
                            type="button"
                            className="saved-search-card-name"
                            onClick={() => navigate(`/search/${s.id}`)}
                          >
                            {s.name}
                          </button>
                          <div className="saved-search-card-description">{s.description}</div>
                          <div className="saved-search-card-meta">
                            <span>recency {formatDecay(s.freshness_constant)}</span>
                            <span>relevance {formatRelevanceMultiplier(s.relevance_constant)}</span>
                            <span>updated {formatRelativeTime(s.updated_at)}</span>
                            <span>{s.feed_enabled ? 'updates every 30 minutes' : 'feed stopped'}</span>
                          </div>
                        </div>
                        <div className="saved-search-card-actions">
                          <button
                            type="button"
                            className="ghost-button"
                            onClick={() => navigate(`/search/${s.id}`)}
                            title="Run this search"
                          >
                            Run
                          </button>
                          <button
                            type="button"
                            className="ghost-button"
                            onClick={() => s.feed_enabled ? void copyFeedUrl(s.id) : void handleFeedEnabled(s, true)}
                            title={s.feed_enabled ? 'Copy this feed\u2019s RSS URL' : undefined}
                          >
                            {s.feed_enabled ? <>Feed URL <IconCopy /></> : 'Make a feed'}
                          </button>
                          {s.feed_enabled && (
                            <DigestSubscriptionMenu
                              descriptionId={s.id}
                              digest={digestsBySearch[s.id]}
                              cadences={cadences}
                              onChanged={loadDigests}
                              onError={(message) => setStatus({ message, isError: true })}
                            />
                          )}
                          {s.feed_enabled && (
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => void handleFeedEnabled(s, false)}
                            >
                              Stop feed
                            </button>
                          )}
                          <button
                            type="button"
                            className="ghost-button"
                            onClick={() => { setEditingId(s.id); setCreating(false); }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="ghost-button saved-search-delete-btn"
                            onClick={() => handleDelete(s.id)}
                            disabled={deletingId === s.id}
                          >
                            {deletingId === s.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
